import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LoggingService } from '@services/logging.service';
import { BDMatrix } from '@models/bdMatrix.model';
import { BDMatrixFilter, ComparableDatabaseFilter } from '@models/bdMatrixFilter.model';
import { BDMatrixEliminationCategory } from '@models/bdMatrixEliminationCategory.model';
import { BDMatrixEliminationCriteria } from '@models/bdMatrixEliminationCriteria.model';
import { BDMatrixPLI } from '@models/bdMatrixPli.model';
import { saveAs } from 'file-saver-es';
import { ProcessDecisionMatrixModel } from '@models/processDescisionMatrix.model';
import { FilterResult } from '@models/filterResult';
import { BulkReject } from '@models/bulkReject.model';
import { firstValueFrom, Observable, catchError, throwError } from 'rxjs';
import { ComparableDatabase } from '@models/comparableDatabase.model';
import { NonBvdComparableDetails } from '../mystudies/analysis/business-description-review/comparable-details/non-bvd-display/non-bvd-comparable-details.model';
import { BDRBulkAcceptReject } from '../mystudies/analysis/business-description-review/bdr-bulk-accept-reject.model';
import { List } from 'linqts-es-module';
import { FinancialDatAdjustmentErrorMessage } from '../shared/_constants';
import { UserNotificationService } from '@services/user-notification.service';

@Injectable({
  providedIn: 'root',
})
export class BdMatrixService {
  public comparableIndex: number;
  public comparableCount: number;
  public filter: BDMatrixFilter = new BDMatrixFilter();
  public isImportCompleted: EventEmitter<boolean> = new EventEmitter();
  public aiLayerEnabled: boolean;

  get itemIndex(): number {
    return this.filter.pageIndex * this.filter.pageSize + this.comparableIndex;
  }

  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private userNotificationService: UserNotificationService,
  ) {}

  public async GetPreliminaryReviewItems(dto: BDMatrixFilter): Promise<FilterResult> {
    this.logger.info(this.constructor.name, 'GetPreliminaryReviewItems', 'Start');

    // const dto: BDMatrixFilter = new BDMatrixFilter();
    // dto.studySearchStrategyId = studySearchStrategyId;
    // dto.pageSize = pageSize;
    // dto.pageIndex = pageIndex;

    const result = <FilterResult>await firstValueFrom(this.http.post<FilterResult>('/api/BDMatrix/GetPreliminaryReviewItemsNew', dto)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetPreliminaryReviewItems', e);
    });

    return result;
  }

  public async getEliminationCriteriaCategory(studySearchStrategyId: number, showInMasterMatrix = false): Promise<BDMatrixEliminationCategory[]> {
    const result = <BDMatrixEliminationCategory[]>await firstValueFrom(
      this.http.get<BDMatrixEliminationCategory[]>(
        '/api/BDMatrix/GetBDMatrixEliminationCategories/' + studySearchStrategyId + '/' + showInMasterMatrix,
      ),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetEliminationCriteriaCategory', e);
    });

    return result;
  }

  public async UpdateBDMatrix(bdMatrix: BDMatrix): Promise<BDMatrix> {
    this.logger.info(this.constructor.name, 'UpdateBDMatrix', 'Start');

    const result = <BDMatrix>await firstValueFrom(this.http.put<BDMatrix>('/api/BDMatrix/UpdateBDMatrix', bdMatrix)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'UpdateBDMatrix', e);
    });

    return result;
  }

  public async GetBDMatrix(bdMatrixFilter: BDMatrixFilter): Promise<FilterResult> {
    this.logger.info(this.constructor.name, 'GetBDMatrix', 'Start');
    const result = <FilterResult>await firstValueFrom(this.http.post<FilterResult>('/api/BDMatrix/GetBDMatrixResult/', bdMatrixFilter)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetBDMatrix', e);
    });

    return result;
  }

  public async GetComparableDatabases(comparableDatabaseFilter: ComparableDatabaseFilter): Promise<ComparableDatabase[]> {
    this.logger.info(this.constructor.name, 'GetComparableDatabases', 'Start');
    const result = <ComparableDatabase[]>await firstValueFrom(
      this.http.post<ComparableDatabase[]>('/api/BDMatrix/GetComparableDatabases/', comparableDatabaseFilter),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetComparableDatabases', e);
    });

    return result;
  }

  public TranslateDescription(studySearchStrategyId: number, comparableId: number): Observable<string> {
    this.logger.info(this.constructor.name, 'GetBDMatrix', 'Start');
    return this.http.post<string>('/api/Study/TranslateDescription/', { studySearchStrategyId: studySearchStrategyId, comparableId: comparableId });
  }

  public GetTranslatedDescription(studySearchStrategyId: number, comparableId: number): Observable<string> {
    this.logger.info(this.constructor.name, 'GetBDMatrix', 'Start');
    return this.http.post<string>('/api/Study/GetTranslatedDescription/', {
      studySearchStrategyId: studySearchStrategyId,
      comparableId: comparableId,
    });
  }

  public async SaveEliminationCriteriaColumn(columnList: BDMatrixEliminationCategory[]) {
    this.logger.info(this.constructor.name, 'SaveEliminationCriteriaColumn', 'Start');
    await firstValueFrom(this.http.post<string>('/api/BDMatrix/SaveRangeBDMatrixEliminationCategory/', columnList)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveEliminationCriteriaColumn', e);
    });
  }

  public async SaveEliminationCriteria(bdMatrixEliminationCriteria: BDMatrixEliminationCriteria) {
    this.logger.info(this.constructor.name, 'SaveEliminationCriteria', 'Start');
    await firstValueFrom(this.http.post<string>('/api/BDMatrix/BDMatrixEliminationCriteria/', bdMatrixEliminationCriteria)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveEliminationCriteria', e);
    });
  }

  public async bulkAcceptReject(bulkAcceptReject: BDRBulkAcceptReject): Promise<boolean> {
    this.logger.info(this.constructor.name, 'BulkAcceptAndReject', 'Start', bulkAcceptReject);
    const saveResult: boolean = <boolean>await firstValueFrom(
      this.http.post<boolean>('/api/BDMatrix/BulkAcceptRejectComparable', bulkAcceptReject),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'BulkAcceptAndReject', e);
    });
    return saveResult;
  }

  public async bulkUpdateBDMatrixReviewedFlag(bulkAcceptReject: BDRBulkAcceptReject): Promise<boolean> {
    this.logger.info(this.constructor.name, 'BulkUpdateBDMatrixReviewedFlag', 'Start', bulkAcceptReject);
    const saveResult: boolean = <boolean>await firstValueFrom(
      this.http.post<boolean>('/api/BDMatrix/BulkUpdateBDMatrixReviewedFlag', bulkAcceptReject),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'BulkUpdateBDMatrixReviewedFlag', e);
    });
    return saveResult;
  }

  public async GetBDPreliminaryResultFooterValues(StudySearchStrategyId: number): Promise<BDMatrixPLI> {
    this.logger.info(this.constructor.name, 'GetBDPreliminaryResultFooterValues', 'Start');

    const preliminaryResultFooterValues = <BDMatrixPLI>await firstValueFrom(
      this.http.get<BDMatrixPLI>(`/api/BDMatrix/GetBDPreliminaryResultFooterValues/${StudySearchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetBDPreliminaryResultFooterValues', e);
    });

    return preliminaryResultFooterValues;
  }

  // to fix issue of Converting circular structure to JSON
  JSONStringify(data: any) {
    let returnData = data;

    try {
      let cache = [];
      returnData = JSON.stringify(data, function (key, value) {
        if (typeof value === 'object' && value !== null) {
          if (cache.indexOf(value) !== -1) {
            // Circular reference found, discard key
            return;
          }
          // Store value in our collection
          cache.push(value);
        }
        return value;
      });
      cache = null; // Enable garbage collection
    } catch (ex) {
      this.logger.error(this.constructor.name, 'JSONStringify', ex);
    }

    return returnData;
  }

  // US28600
  public async GetProcessDecisionMatrixResult(filter: BDMatrixFilter): Promise<ProcessDecisionMatrixModel[]> {
    this.logger.info(this.constructor.name, 'GetProcessDecisionMatrixResult', 'Start');

    const result = <ProcessDecisionMatrixModel[]>await firstValueFrom(
      this.http.post<ProcessDecisionMatrixModel[]>(`/api/BDMatrix/GetProcessDecisionMatrix`, filter),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetProcessDecisionMatrixResult', e);
    });

    return result;
  }

  public getItem(searchStrategyId: number): Promise<FilterResult> {
    this.logger.info(this.constructor.name, 'getItem', 'Start');
    const itemIndex = this.filter.pageIndex * this.filter.pageSize + this.comparableIndex;
    const filter = Object.assign({}, this.filter);
    filter.pageSize = 1;
    filter.pageIndex = itemIndex;
    filter.studySearchStrategyId = searchStrategyId;
    return this.GetBDMatrix(filter);
  }

  public async getNonBvdComparableDetails(
    comparableId: number,
    databaseId: number,
    feedId: number,
    searchStrategyId: number,
  ): Promise<NonBvdComparableDetails> {
    this.logger.info(this.constructor.name, 'getNonBvdComparableDetails', 'Start');

    const result = <NonBvdComparableDetails>await firstValueFrom(
      this.http.get<NonBvdComparableDetails>(
        '/api/BDMatrix/GetNonBvdComparableDetails/?comparableId=' +
          comparableId +
          '&databaseId=' +
          databaseId +
          '&feedId=' +
          feedId +
          '&searchStrategyId=' +
          searchStrategyId,
      ),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetNonBvdComparableDetails', e);
    });

    return result;
  }

  public async bulkRejectComparableWithEliminationCriteria(bulkReject: BulkReject): Promise<boolean> {
    this.logger.info(this.constructor.name, 'BulkRejectComparableWithEliminationCriteria', 'Start');

    const result = <boolean>await firstValueFrom(
      this.http.put<boolean>('/api/BDMatrix/BulkRejectComparableWithEliminationCriteria', bulkReject),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'BulkRejectComparableWithEliminationCriteria', e);
    });

    return result;
  }

  public TranslateSearchResults(studySearchStrategyId: number, comparableIdList: List<number>): Observable<void> {
    this.logger.info(this.constructor.name, 'TranslateSearchResults', 'Start');
    return this.http.post<void>('/api/Study/TranslateSearchResults/', {
      studySearchStrategyId: studySearchStrategyId,
      comparableIdList: comparableIdList.ToArray(),
    });
  }

  public excludeCompWithMissingBD(studySearchStrategyId: number): Observable<boolean> {
    this.logger.info(this.constructor.name, 'ExcludeCompWithMissingBD', 'Start');
    return this.http.put<boolean>('/api/BDMatrix/ExcludeCompWithMissingBD/', studySearchStrategyId);
  }

  public CompaniesWithMissingBDExcluded(studySearchStrategyId: number): Observable<boolean> {
    this.logger.info(this.constructor.name, 'CompaniesWithMissingBDExcluded', 'Start');
    return this.http.get<boolean>('/api/BDMatrix/CompaniesWithMissingBDExcluded/?studySearchStrategyId=' + studySearchStrategyId);
  }

  async exportMatrix(studyId: number): Promise<boolean> {
    const result = await firstValueFrom(
      this.http.get('/api/BDMatrix/ExportMatrix/' + studyId, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then((response) => {
        let filename = 'Temp_';
        const contentDispositionHeader = response.headers.get('Content-Disposition');

        if (contentDispositionHeader) {
          filename = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        }

        const blob = new Blob([response.body], { type: response.body.type.toString() });
        saveAs(blob, filename);

        this.userNotificationService.addNotification(`Business Description Matrix Excel file for ${studyId}: Ready to download.`, false);
        return true;
      })
      .catch((e) => {
        this.logger.httpError(this.constructor.name, 'exportMatrix', e);
        if (e.status === 404) {
          this.userNotificationService.addNotification(
            `Business Description Matrix Excel file failed to download for study ${studyId}: No data found.`,
            true,
          );
        }

        return false;
      });
    return result;
  }

  aiLayerEnabledStatus(): boolean {
    return this.aiLayerEnabled;
  }
  sendAilayerStatus(value) {
    this.aiLayerEnabled = value;
  }

  async importMatrix(formdata: FormData, returnurl: string, studyId: number): Promise<boolean> {
    const fileName = formdata.get('fileName');
    this.userNotificationService.addNotification(
      `Import in Progress: Your file '${fileName}' is being imported into Business Description Matrix. Please note that this process may take a few moments, particularly for larger files. for studyId  ${studyId}.`,
      false,
    );

    const result = await firstValueFrom(this.http.post('/api/BDMatrix/importmatrix/', formdata))
      .then(() => {
        this.userNotificationService.addNotification(
          `Import Complete: Your file '${fileName}' has been successfully imported into Business Description Matrix for studyId  ${studyId}.`,
          false,
        );
        this.isImportCompleted.emit(true);
        return true;
      })
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export for study ${studyId}: No data found.`,
            true,
          );
          return null;
        } else {
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export ${studyId}:${resultMsg}`,
            true,
          );
        }
        return false;
      });
    return result;
  }
  public resetBDMatrixDataOnAITurnOff(studySearchStrategyId: number): Observable<boolean> {
    this.logger.info(this.constructor.name, 'ResetBDMatrixDataOnAITurnOff', 'Start');
    return this.http.put<boolean>('/api/BDMatrix/ResetBDMatrixDataOnAITurnOff/', studySearchStrategyId).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, '/api/BDMatrix/ResetBDMatrixDataOnAITurnOff/', error);
        return throwError(() => error);
      }),
    );
  }
}
